/**
 * 报名按钮处理
 */
import React, { useCallback, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import PublicEventGetEventInfo from '@quickService/PublicEventGetEventInfo';
import dynamic from 'next/dynamic';
import { Modal, message } from '@tencent/tea-component';
import { isPad, isMobile, isWechat, storage, isWechatMiniProgram } from '@tencent/web-base';
import { AppContext, ROLETYPE } from '@utils/browser/app-context';
import { goLogin } from 'src/utils/login-util';
import { CouponsExtend } from '@model/buyer-coupon';
import './signUp.scss';
import { MyScheduleIconContext } from '../buyer/CanvasRender';
import BuyTicketModal from '@tencent/calendar-web-component-mobile-biz/es/BuyTicketModal';
import http from '@lib/http';
import dataReport from '@utils/data-report';
import onClickLaunch from '@utils/launch/index';
import { useAccountInfo } from 'src/hooks/useAccount';
import RegisterTicketListTicket from '@quickService/RegisterTicketListTicket';
import PublicRegistrationQueryEventSchedulers from '@quickService/PublicRegistrationQueryEventSchedulers';
import PublicCouponQueryOne from '@quickService/PublicCouponQueryOne';
import { Data as RegistrationData } from '@quickService/ActivitiesPublicRegistrationPage';

const PcBuyModal = dynamic(
  () => import('@tencent/calendar-web-component-pc-biz/es/BuyModal/index'),
  {
    ssr: false,
  },
);

let qrModal: any;

const useSignUp = (couponList?: CouponsExtend, wxopenId?: string | undefined) => {
  const { query, push } = useRouter();
  const { registerHostData, hostDetail, userData, setUserData } = useContext(AppContext);

  const { setMyScheduleIconState } = useContext(MyScheduleIconContext);
  const userInfo = useAccountInfo();
  useEffect(
    () => () => {
      qrModal?.destroy();
      qrModal = undefined;
    },
    [],
  );

  const paySuccessCb = useCallback(() => {
    setMyScheduleIconState((p: any) => ({ ...p, animated: true }));
  }, [setMyScheduleIconState]);
  return async (
    eventId: any,
    scheduleId?: any,
    type?: any,
    step?: number,
    couponId?: any,
    setBtnLoading?: Function,
    registerData?: RegistrationData,
  ) => {
    if (!(userData.app_uid || userData.userid)) {
      if (isWechat() && window.sessionStorage) {
        const params = JSON.stringify({
          s_id: scheduleId || '',
          t: type || '',
        });
        sessionStorage.setItem('redirectParams', params);
      }
      goLogin();
      return;
    }

    if (
      registerHostData?.host_id === hostDetail.host_id &&
      hostDetail?.role_id === ROLETYPE.CREATED
    ) {
      message.error({
        content: '无法报名自己的活动',
      });
      return;
    }
    // 防止报名时活动被屏蔽
    const { code, data } = await PublicEventGetEventInfo({ event_id: eventId, source: 0 });

    if (code === 0 && !data.event_info) {
      message.error({
        content: '活动信息不存在',
      });
      return;
    }
    const queryData = `?s_id=${scheduleId || ''}&t=${type || ''}&ch=${query?.ch || ''}&nh=${
      query?.nh || ''
    }&source=${query?.source || ''}&source_identifier=${query?.source_identifier || ''}&site_type=${
      query?.site_type || ''
    }`;
    let path = `/tickets/${eventId}?${queryData}`;
    // 显示成移动端的话，就走移动端页面
    const w = window.innerWidth;

    // 科学小会需求，普通报名页等门票数据拿到以后再拉起报名弹框
    let parentTicketData = undefined;
    const signUpFrom = sessionStorage.getItem('sign-up-from');
    const redirectParams = sessionStorage.getItem('redirectParams');

    if (registerData || (redirectParams && signUpFrom === 'registerBtn')) {
      setBtnLoading?.(true);

      const queryMessage: any = [
        RegisterTicketListTicket({
          event_id: eventId || '',
          schedule_id: '',
        }),
        PublicRegistrationQueryEventSchedulers({
          event_id: eventId,
          guest_image_type: 'small',
          scheduler_pic_image_type: 'small',
        }),
      ];

      couponId &&
        queryMessage.push(
          PublicCouponQueryOne({
            coupon_id: couponId,
          }),
        );

      parentTicketData = await Promise.all(queryMessage);

      setBtnLoading?.(false);

      sessionStorage.removeItem('sign-up-from');
    }

    if (isPad() || isMobile() || w <= 768) {
      if (isWechatMiniProgram()) {
        // 微信小程序webview内访问
        const nextUrl = `/emptyPay?eventId=${eventId}&${queryData}`;
        const mpRedirectUrl = `/registration/pages/emptyPay/index?type=jump&targeturl=${encodeURIComponent(
          nextUrl,
        )}`;
        window.wx.miniProgram.navigateTo({
          url: mpRedirectUrl,
        });

        return;
      }
      // 服务端判断会有点问题，加上一些前置的兜底
      if (isWechat()) {
        path = `${path}&wx=1`;

        window.parent.postMessage(
          {
            type: 'openWindow',
            href: `${window.location.origin}/events/host${path}`,
          },
          '*',
        );
      }
      if (query?.source === 'assistant') {
        push(path);
        return;
      }

      // 防止在lobby购票后又在活动购票，购票完成页判断有误
      storage.useStorage('sessionStorage').removeItem('lobby_meeting_info');

      return BuyTicketModal({
        visible: true,
        http,
        dataReport,
        eventId,
        userData,
        wxopenId: wxopenId || '',
        query: {
          s_id: scheduleId || '',
          t: type || '',
          ...query,
        },
        couponList,
        onClickLaunch,
        userInfo,
        hostDetail,
        eventInfo: data.event_info,
        close: () => {
          setUserData({
            ...userData,
            goAndUpdateTicket: true,
            updateCoupon: true,
          });
        },
        type: 'activity',
        parentTicketData,
        registerData,
      });
    }

    if (qrModal) {
      return;
    }

    qrModal = Modal.show({
      size: 'auto',
      className: 'event-sign-up',
      // @ts-ignore
      children: (
        <PcBuyModal
          eventId={eventId}
          scheduleId={scheduleId}
          ticketType={type}
          userData={userData}
          pageStep={step}
          close={() => {
            qrModal.destroy();
            qrModal = undefined;
            setUserData({
              ...userData,
              updateCoupon: true,
            });
          }}
          couponList={couponList}
          couponId={couponId}
          updateMyTickets={() => {
            setUserData({
              ...userData,
              goAndUpdateTicket: true,
              updateCoupon: true,
            });
          }}
          http={http}
          dataReport={dataReport}
          onClickLaunch={onClickLaunch}
          paySuccessCb={paySuccessCb}
          parentTicketData={parentTicketData}
          registerData={registerData}
          eventInfo={data.event_info}
          // showLeftDetailBtn
        />
      ),
      onClose: () => {
        setUserData({
          ...userData,
          goAndUpdateTicket: true,
          updateCoupon: true,
        });
        setMyScheduleIconState((p: any) => ({ ...p, show: false }));
        qrModal.destroy();
        qrModal = undefined;
      },
      // disableCloseIcon: true,
    });

    return qrModal;
  };
};

export default useSignUp;
